/**
 * Receipts tab groups. A row is in one group.
 * Needs you outranks the others, including a hold on a bill that is already in Xero.
 * Resolved is in Xero, the recharge question is answered, and no Xero
 * contact, total, or date mismatch is still set. A live mismatch is Needs you
 * until the next read-back clears it.
 * rechargeable null is not an answer.
 * A manual resolve is placement only and outranks every rule below.
 * Unresolve clears that mark and these rules place the row again.
 */
import { sterlingGrossPence } from './currency.js';
import { receiptPushDisplayState } from './enqueueAccpay.js';
import { differingBillDatesLabel } from './receiptDate.js';

export const RECEIPT_GROUPS = [
  'needs_you',
  'ready_to_send',
  'in_progress',
  'unrecharged',
  'resolved',
] as const;

export type ReceiptGroupId = (typeof RECEIPT_GROUPS)[number];

export const RECEIPT_GROUP_LABEL: Record<ReceiptGroupId, string> = {
  needs_you: 'Needs you',
  ready_to_send: 'Ready to send',
  in_progress: 'In progress',
  unrecharged: 'Unrecharged',
  resolved: 'Resolved',
};

export type ReceiptForGroup = {
  status: string;
  xero_bill_id?: string | null;
  xero_push_outcome?: string | null;
  held?: boolean | null;
  money_reconcile_overridden?: boolean | null;
  rechargeable?: boolean | null;
  recharge_job_id?: string | null;
  currency?: string | null;
  gross_pence?: number | null;
  gbp_gross_pence?: number | null;
  xero_date_mismatch?: boolean | null;
  xero_total_mismatch?: boolean | null;
  xero_contact_map_mismatch?: boolean | null;
  receipt_date?: string | null;
  xero_bill_date?: string | null;
  /** Set when Jack resolved the row himself. Placement only. */
  manual_resolved_at?: string | Date | null;
};

/** Flag labels, in date / total / contact order. Empty when the read-back is clean. */
export function xeroMismatchLabels(receipt: ReceiptForGroup): string[] {
  const labels: string[] = [];
  if (receipt.xero_date_mismatch) {
    labels.push(
      differingBillDatesLabel(receipt.receipt_date, receipt.xero_bill_date) ??
        'Xero date differs',
    );
  }
  if (receipt.xero_total_mismatch) labels.push('Xero total differs');
  if (receipt.xero_contact_map_mismatch) labels.push('Xero contact differs');
  return labels;
}

export function receiptInXero(receipt: ReceiptForGroup): boolean {
  return receipt.status === 'sent_to_accounts' || Boolean(receipt.xero_bill_id);
}

/** false, or true with a recharge job. null is unanswered. */
export function rechargeAnswered(receipt: ReceiptForGroup): boolean {
  if (receipt.rechargeable === false) return true;
  return receipt.rechargeable === true && Boolean(receipt.recharge_job_id);
}

export function receiptGroup(receipt: ReceiptForGroup): ReceiptGroupId {
  if (receipt.manual_resolved_at) return 'resolved';
  const display = receiptPushDisplayState(receipt);
  if (
    receipt.status === 'awaiting_review' ||
    display === 'action_needed' ||
    display === 'failed' ||
    display === 'unknown'
  ) {
    return 'needs_you';
  }
  if (
    receipt.status === 'pending_upload' ||
    receipt.status === 'pending_extraction' ||
    display === 'queued'
  ) {
    return 'in_progress';
  }
  if (!receiptInXero(receipt)) return 'ready_to_send';
  if (!rechargeAnswered(receipt)) return 'unrecharged';
  if (xeroMismatchLabels(receipt).length > 0) return 'needs_you';
  return 'resolved';
}

/**
 * Survives the archive filter. Ready to send and in progress are visible
 * groups, but they are not this count — those either wait on a tap to send
 * or finish by themselves.
 */
export function receiptNeedsMe(receipt: ReceiptForGroup): boolean {
  const group = receiptGroup(receipt);
  return group === 'needs_you' || group === 'unrecharged';
}

export function unrechargedSterlingPence(rows: ReceiptForGroup[]): number {
  let total = 0;
  for (const row of rows) {
    if (receiptGroup(row) !== 'unrecharged') continue;
    total +=
      sterlingGrossPence({
        currency: row.currency,
        gross_pence: row.gross_pence,
        gbp_gross_pence: row.gbp_gross_pence,
      }) ?? 0;
  }
  return total;
}
